import axios from 'axios';
import {
  ShowScan,
  ShowSummary,
  ShowAnalytics,
  TrendData,
  DayOfWeekStats,
  TktsHoursResponse,
  MatineeTrendData,
  ShowMovementResponse,
  ShowRecentAppearances,
  MasterShow,
  CreateMasterShowRequest,
  LiveDealsResponse,
  LocationAvailability,
  ShowMetadataResponse,
  IbdbCandidate,
  SpectraCandidate,
  WeeklyAvailability,
} from '../types';

// In production (Docker), use relative URL so nginx can proxy
// In development, use localhost:8001
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8001');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests when available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token && config.url?.includes('/api/admin/') && !config.url?.endsWith('/login')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses by clearing token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.config?.url?.includes('/api/admin/')) {
      localStorage.removeItem('admin_token');
    }
    return Promise.reject(error);
  }
);

// Get current shows (latest scan)
export const getCurrentShows = async (
  location?: string,
  category?: string
): Promise<ShowScan[]> => {
  const params = new URLSearchParams();
  if (location) params.append('location', location);
  if (category) params.append('category', category);
  const response = await api.get(`/api/shows/current?${params}`);
  return response.data;
};

// Get all shows with summary stats
export const getShowsList = async (): Promise<ShowSummary[]> => {
  const response = await api.get('/api/shows');
  return response.data;
};

// Get analytics for a specific show
export const getShowAnalytics = async (showName: string): Promise<ShowAnalytics> => {
  const response = await api.get(`/api/analytics/shows/${encodeURIComponent(showName)}`);
  return response.data;
};

// Get historical scans for a show
export const getShowHistory = async (
  showName: string,
  limit?: number
): Promise<ShowScan[]> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  const response = await api.get(
    `/api/shows/${encodeURIComponent(showName)}/history?${params}`
  );
  return response.data;
};

// Get trend data over time (optionally filtered by show)
export const getTrends = async (days?: number, showName?: string): Promise<TrendData[]> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  if (showName) params.append('show_name', showName);
  const response = await api.get(`/api/analytics/trends?${params}`);
  return response.data;
};

// Get day of week statistics
export const getDayOfWeekStats = async (days?: number): Promise<DayOfWeekStats[]> => {
  const response = await api.get(`/api/analytics/day-of-week${days ? `?days=${days}` : ''}`);
  return response.data;
};

export const getWeeklyAvailability = async (weeks = 12): Promise<WeeklyAvailability[]> => {
  const response = await api.get(`/api/analytics/weekly-availability?weeks=${weeks}`);
  return response.data;
};

// Get TKTS booth operating hours
export const getTktsHours = async (): Promise<TktsHoursResponse> => {
  const response = await api.get('/api/analytics/tkts-hours');
  return response.data;
};

// Get list of all show names (for filtering)
export const getShowNames = async (): Promise<string[]> => {
  const response = await api.get('/api/analytics/show-names');
  return response.data;
};

// Get matinee vs regular show trends
export const getMatineeTrends = async (days?: number): Promise<MatineeTrendData[]> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  const response = await api.get(`/api/analytics/matinee-trends?${params}`);
  return response.data;
};

// Get show movement data (late additions, early removals)
export const getShowMovement = async (days?: number): Promise<ShowMovementResponse> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  const response = await api.get(`/api/analytics/show-movement?${params}`);
  return response.data;
};

// Get recent appearances for a specific show
export const getShowRecentAppearances = async (
  showName: string,
  days?: number,
  location?: string
): Promise<ShowRecentAppearances> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  if (location) params.append('location', location);
  const response = await api.get(
    `/api/analytics/shows/${encodeURIComponent(showName)}/recent-appearances?${params}`
  );
  return response.data;
};

// Master Shows API
export const getMasterShows = async (): Promise<MasterShow[]> => {
  const response = await api.get('/api/admin/master-shows');
  return response.data;
};

export const createMasterShow = async (data: CreateMasterShowRequest): Promise<MasterShow> => {
  const response = await api.post('/api/admin/master-shows', data);
  return response.data;
};

export const updateMasterShow = async (id: number, data: Partial<CreateMasterShowRequest>): Promise<MasterShow> => {
  const response = await api.put(`/api/admin/master-shows/${id}`, data);
  return response.data;
};

export const deleteMasterShow = async (id: number): Promise<void> => {
  await api.delete(`/api/admin/master-shows/${id}`);
};

export const unlinkShowAlias = async (masterShowId: number, alias: string): Promise<void> => {
  await api.delete(`/api/admin/master-shows/${masterShowId}/aliases/${encodeURIComponent(alias)}`);
};

// IBDB Search & Fetch
export const searchIbdb = async (query: string): Promise<IbdbCandidate[]> => {
  const response = await api.get(`/api/admin/ibdb-search?q=${encodeURIComponent(query)}`);
  return response.data;
};

export const fetchIbdbMetadata = async (
  masterShowId: number,
  ibdbId: string,
  productionType: string
): Promise<MasterShow> => {
  const response = await api.post(`/api/admin/master-shows/${masterShowId}/fetch-ibdb`, {
    ibdb_id: ibdbId,
    production_type: productionType,
  });
  return response.data;
};

export const autoMatchIbdb = async (masterShowId: number): Promise<MasterShow> => {
  const response = await api.post(`/api/admin/master-shows/${masterShowId}/auto-match-ibdb`);
  return response.data;
};

export const searchSpectra = async (query: string): Promise<SpectraCandidate[]> => {
  const response = await api.get(`/api/admin/spectra-search?q=${encodeURIComponent(query)}`);
  return response.data;
};

export const fetchSpectraMetadata = async (
  masterShowId: number,
  spectraId: string,
): Promise<MasterShow> => {
  const response = await api.post(`/api/admin/master-shows/${masterShowId}/fetch-spectra`, {
    spectra_id: spectraId,
  });
  return response.data;
};

export const bulkAutoMatchIbdb = async (): Promise<{ message: string; count: number }> => {
  const response = await api.post('/api/admin/bulk-auto-match-ibdb');
  return response.data;
};

export interface ManualMetadataRequest {
  preview_date?: string | null;
  opening_date?: string | null;
  close_date?: string | null;
  theatre?: string | null;
  director?: string | null;
  composer?: string | null;
  cast_info?: string | null;
  open_run?: boolean;  // true pins the show open-ended; false releases that pin
}

export const setManualMetadata = async (
  id: number,
  data: ManualMetadataRequest
): Promise<MasterShow> => {
  const response = await api.patch(`/api/admin/master-shows/${id}/metadata`, data);
  return response.data;
};

export const setMasterShowCategory = async (
  id: number,
  category: 'Broadway' | 'Off-Broadway' | null
): Promise<MasterShow & { typical_category: string | null }> => {
  const response = await api.post(`/api/admin/master-shows/${id}/set-category`, { category });
  return response.data;
};

export interface BulkRefreshFilters {
  include_ibdb: boolean;
  include_spectra: boolean;
  include_open: boolean;
  include_closed: boolean;
}

export const bulkRefreshIbdb = async (filters?: BulkRefreshFilters): Promise<{ message: string; count: number }> => {
  const response = await api.post('/api/admin/bulk-refresh-ibdb', filters ?? {});
  return response.data;
};

export interface BulkMatchStatus {
  running: boolean;
  total: number;
  done: number;
  current: string;
  eta_seconds: number;
  started_at: string | null;
}

export const getBulkMatchStatus = async (): Promise<BulkMatchStatus> => {
  const response = await api.get('/api/admin/bulk-auto-match-ibdb/status');
  return response.data;
};

export const confirmIbdbMatch = async (masterShowId: number): Promise<MasterShow> => {
  const response = await api.post(`/api/admin/master-shows/${masterShowId}/confirm-ibdb`);
  return response.data;
};

export const backfillMasterShows = async (): Promise<{ message: string; created: number; total: number }> => {
  const response = await api.post('/api/admin/backfill-master-shows');
  return response.data;
};

// Public show metadata
export const getShowMetadata = async (showName: string): Promise<ShowMetadataResponse> => {
  const response = await api.get(`/api/shows/${encodeURIComponent(showName)}/metadata`);
  return response.data;
};

// Show status flags (date-based badges for dashboard)
export interface ShowStatusEntry {
  canonical_name: string;
  preview_date: string | null;
  opening_date: string | null;
  close_date: string | null;
}

export const getShowStatusFlags = async (): Promise<Record<string, ShowStatusEntry>> => {
  const response = await api.get('/api/shows/status-flags');
  return response.data;
};

// Get location availability breakdown for a show
export const getShowLocationAvailability = async (showName: string): Promise<LocationAvailability> => {
  const response = await api.get(
    `/api/analytics/shows/${encodeURIComponent(showName)}/location-availability`
  );
  return response.data;
};

// Get live deals with sold out tracking
export const getLiveDeals = async (
  location?: string,
  category?: string
): Promise<LiveDealsResponse> => {
  const params = new URLSearchParams();
  if (location) params.append('location', location);
  if (category) params.append('category', category);
  const response = await api.get(`/api/analytics/live-deals?${params}`);
  return response.data;
};

// Price volatility response type
export interface PriceVolatilityShow {
  show_name: string;
  std_dev: number;
  avg_price: number;
  min_price: number;
  max_price: number;
  price_range: number;
  days_seen: number;
}

export interface PriceVolatilityResponse {
  shows: PriceVolatilityShow[];
  analysis_period_days: number;
}

// Get shows with highest price volatility
export const getPriceVolatility = async (days?: number): Promise<PriceVolatilityResponse> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  const response = await api.get(`/api/analytics/price-volatility?${params}`);
  return response.data;
};

// New shows response type
export interface NewShow {
  show_name: string;
  category: string;
  first_seen: string;
  days_appeared: number;
  avg_discount: number;
  avg_price: number;
}

export interface NewShowsResponse {
  shows: NewShow[];
  analysis_period_days: number;
}

// Get shows that recently started appearing at TKTS
export const getNewShows = async (days?: number): Promise<NewShowsResponse> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  const response = await api.get(`/api/analytics/new-shows?${params}`);
  return response.data;
};

// Disappearing shows response type
export interface DisappearingShow {
  show_name: string;
  category: string;
  close_date: string | null;
  last_seen: string;
  days_since_seen: number;
  days_appeared: number;
  avg_discount: number;
  avg_price: number;
}

export interface DisappearingShowsResponse {
  shows: DisappearingShow[];
  analysis_period_days: number;
  absent_days_threshold: number;
}

// Get shows that used to appear frequently but haven't been seen recently
export const getDisappearingShows = async (days?: number, absentDays?: number): Promise<DisappearingShowsResponse> => {
  const params = new URLSearchParams();
  if (days) params.append('days', days.toString());
  if (absentDays) params.append('absent_days', absentDays.toString());
  const response = await api.get(`/api/analytics/disappearing-shows?${params}`);
  return response.data;
};

// Blackout windows (periods of missing/known-bad scan data, excluded from all stats)
export interface BlackoutWindow {
  id: number;
  starts_at: string;
  ends_at: string;
  location: string | null;
  reason: string;
  created_at: string | null;
}

export const getBlackoutWindows = async (): Promise<BlackoutWindow[]> => {
  const response = await api.get('/api/analytics/blackout-windows');
  return response.data;
};

export const createBlackoutWindow = async (data: {
  starts_at: string;
  ends_at: string;
  location: string | null;
  reason: string;
}): Promise<BlackoutWindow> => {
  const response = await api.post('/api/admin/blackout-windows', data);
  return response.data;
};

export const deleteBlackoutWindow = async (id: number): Promise<void> => {
  await api.delete(`/api/admin/blackout-windows/${id}`);
};

// Admin Authentication
export const adminLogin = async (password: string): Promise<{ token: string }> => {
  const response = await api.post('/api/admin/login', { password });
  return response.data;
};

// Category Issues
export interface CategoryIssue {
  show_name: string;
  categories: Array<{
    category: string;
    count: number;
    first_seen: string;
    last_seen: string;
  }>;
  likely_correct: string;
}

export const getCategoryIssues = async (): Promise<CategoryIssue[]> => {
  const response = await api.get('/api/admin/category-issues');
  return response.data;
};

export const fixCategory = async (
  showName: string,
  correctCategory: string,
  scanDate?: string
): Promise<{ message: string }> => {
  const response = await api.post('/api/admin/fix-category', {
    show_name: showName,
    correct_category: correctCategory,
    scan_date: scanDate || undefined,
  });
  return response.data;
};

// Duplicate Shows
export interface DuplicateShow {
  normalized_key: string;
  variants: Array<{ name: string; count: number }>;
  suggested_target: string;
}

export const getDuplicateShows = async (): Promise<DuplicateShow[]> => {
  const response = await api.get('/api/admin/duplicate-shows');
  return response.data;
};

export interface ClosedButActiveShow {
  master_show_id: number;
  show_name: string;
  close_date: string;
  latest_performance: string;
  days_after: number;
  scan_count: number;
}

export const getClosedButActive = async (): Promise<ClosedButActiveShow[]> => {
  const response = await api.get('/api/admin/shows-closed-but-active');
  return response.data;
};

export const resolveStaleClose = async (
  id: number,
  action: 'clear_close_date' | 'delete_post_close_scans'
): Promise<{ status: string; excluded?: number }> => {
  const response = await api.post(`/api/admin/master-shows/${id}/resolve-stale-close`, { action });
  return response.data;
};

export const mergeShows = async (
  sourceName: string,
  targetName: string
): Promise<{ message: string }> => {
  const response = await api.post('/api/admin/merge-shows', {
    source_name: sourceName,
    target_name: targetName,
  });
  return response.data;
};

export default api;
